<template>
    <li>
        <div class="uk-grid">
            <div class="uk-width-1-2">
                <div class="uk-panel uk-panel-box">
                    <h3 class="uk-panel-title">Project Keywords</h3>

                    <ul class="uk-list uk-list-line">
                        <li
                            v-for="keyword_section in keyword_sections"
                            :key="keyword_section.id"
                        >
                            <div class="uk-grid uk-grid-small">
                                <div class="uk-width-9-10">
                                    <a href @click.prevent="keyword_section_edit(keyword_section)">{{ keyword_section.section }}</a>
                                </div>
                                <div class="uk-width-1-10 uk-text-right">
                                    <base-button
                                        color="danger"
                                        size="mini"
                                        @click.prevent="keyword_section_remove(keyword_section)"
                                    >
                                        <open-icon glyph="trash" />
                                    </base-button>
                                </div>
                            </div>
                        </li>
                        <li>
                            <form @submit.prevent.stop class="uk-form uk-grid uk-grid-small">
                                <div class="uk-width-9-10">
                                    <form-input
                                        id="new_keyword_section"
                                        name="new_keyword_section"
                                        v-model="new_keyword_section"
                                        placeholder="New Section's Name"
                                        :editable="true"
                                        :required="false"
                                        :error="has_error('new_keyword_section')"
                                        class="uk-width-1-1"
                                    />
                                </div>
                                <div class="uk-width-1-10">
                                    <base-button
                                        color="primary"
                                        @click.prevent="keyword_section_add"
                                    >
                                        <open-icon glyph="plus" />
                                    </base-button>
                                </div>
                            </form>
                        </li>
                    </ul>
                </div>

                <div class="uk-panel uk-panel-box">
                    <h3 class="uk-panel-title">Dictionaries</h3>

                    <ul class="uk-list uk-list-line">
                        <li v-for="dictionary in sorted_dictionaries" :key="dictionary.id">
                            <a href @click.prevent="dictionary_edit(dictionary)">{{ dictionary.name }}</a>
                        </li>
                    </ul>
                </div>

                <div class="uk-panel uk-panel-box">
                    <h3 class="uk-panel-title">Autosuggest</h3>

                    <ul class="uk-list uk-list-line">
                        <li v-for="dictionary in sorted_autosuggests" :key="dictionary.id">
                            <a href @click.prevent="dictionary_edit(dictionary)">{{ dictionary.name }}</a>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="uk-width-1-2">
                <div class="uk-panel uk-panel-box">
                    <h3 class="uk-panel-title">Services</h3>

                    <ul class="uk-list uk-list-line">
                        <li
                            v-for="service in sorted_services"
                            :key="service.id"
                        >
                            <div class="uk-grid uk-grid-small">
                                <div class="uk-width-9-10">
                                    <a href @click.prevent="service_edit(service)">{{ service.name }}</a>
                                </div>
                                <div class="uk-width-1-10 uk-text-right">
                                    <base-button
                                        color="danger"
                                        size="mini"
                                        @click.prevent="service_remove(service)"
                                    >
                                        <open-icon glyph="trash" />
                                    </base-button>
                                </div>
                            </div>
                        </li>
                        <li>
                            <form class="uk-form uk-grid uk-grid-small">
                                <div class="uk-width-9-10">
                                    <form-input
                                        id="new_service"
                                        name="new_service"
                                        v-model="new_service"
                                        placeholder="New Service's Name"
                                        :editable="true"
                                        :required="false"
                                        :error="has_error('new_service')"
                                        class="uk-width-1-1"
                                    />
                                </div>
                                <div class="uk-width-1-10">
                                    <base-button
                                        color="primary"
                                        @click.prevent="service_add"
                                    >
                                        <open-icon glyph="plus" />
                                    </base-button>
                                </div>
                            </form>
                        </li>
                    </ul>
                </div>
                <awards />
            </div>
        </div>

        <modal-dialog
            id="keyword-editor"
            :show.sync="editing_keyword_section"
        >
            <template v-slot:title>
                Keyword section "{{ edited_keyword_section.section }}"
            </template>

            <form @submit.prevent.stop class="uk-form">
                <div
                    v-if="!is_main_keyword_section()"
                    class="uk-grid uk-grid-small"
                >
                    <div class="uk-width-1-1">
                        <form-input
                            id="section"
                            name="section"
                            v-model="edited_keyword_section.section"
                            :editable="true"
                            :required="false"
                            class="uk-width-1-1"
                            @input="keyword_section_change"
                        />
                    </div>
                </div>

                <ul class="uk-list uk-list-space">
                    <li
                        v-for="keyword in edited_keyword_section_keywords"
                        :key="keyword.id"
                    >
                        <div class="uk-grid uk-grid-small">
                            <div class="uk-width-9-10">
                                <form-input
                                    :id="`keyword-${keyword.id}-name`"
                                    name="name"
                                    v-model="keyword.name"
                                    :editable="true"
                                    :required="false"
                                    class="uk-width-1-1"
                                    @input="save_field_for_record_id('category', keyword.id, $event, 'name')"
                                >
                                    <template v-slot:hint>
                                        <span class="uk-text-small">
                                            {{ keyword.project_count }} project(s)
                                        </span>
                                    </template>
                                </form-input>
                            </div>
                            <div class="uk-width-1-10">
                                <base-button
                                    color="danger"
                                    :disabled="!keyword_section_keyword_is_deletable(keyword)"
                                    @click.prevent="keyword_section_keyword_remove(keyword)"
                                >
                                    <open-icon glyph="trash" />
                                </base-button>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="uk-grid uk-grid-small">
                            <div class="uk-width-9-10">
                                <form-input
                                    id="new-keyword"
                                    name="name"
                                    v-model="new_keyword.name"
                                    :editable="true"
                                    :required="false"
                                    class="uk-width-1-1"
                                />
                            </div>
                            <div class="uk-width-1-10">
                                <base-button
                                    color="primary"
                                    @click.prevent="keyword_section_keyword_add"
                                >
                                    <open-icon glyph="plus" />
                                </base-button>
                            </div>
                        </div>
                    </li>
                </ul>
            </form>
        </modal-dialog>

        <modal-dialog
            id="dictionary-editor"
            :show.sync="editing_dictionay"
        >
            <template v-slot:title>
                Dictionary "{{ edited_dictionary.name }}"
            </template>

            <form @submit.prevent.stop class="uk-form">
                <ul class="uk-list uk-list-space">
                    <li class="uk-text-small">
                        <div class="uk-grid uk-grid-small">
                            <div v-if="edited_dictionary_can_have_default" class="uk-width-1-10 uk-text-center">
                                <strong>Default</strong>
                            </div>
                            <div
                                :class="{
                                    'uk-width-8-10': (edited_dictionary_term_number_of_buttons() === 2),
                                    'uk-width-9-10': (edited_dictionary_term_number_of_buttons() === 1),
                                    'uk-width-1-1': (edited_dictionary_term_number_of_buttons() === 0)
                                }"
                            >
                                <div class="uk-grid uk-grid-small">
                                    <div :class="{ 'uk-width-8-10': edited_dictionary_can_have_weights, 'uk-width-1-1': !edited_dictionary_can_have_weights }">
                                        <strong>Value</strong>
                                    </div>
                                    <div v-if="edited_dictionary_can_have_weights" class="uk-width-2-10">
                                        <strong>Weight</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                    <li
                        v-for="term in edited_dictionary_terms"
                        :key="term.id"
                    >
                        <div class="uk-grid uk-grid-small">
                            <div v-if="edited_dictionary_can_have_default" class="uk-width-1-10 uk-text-center">
                                <a
                                    href
                                    :title="term.default_value?'This is the default value':'Make this term the default value for this dictionary'"
                                    :class="{'uk-active': term.default_value, 'uk-invisible': !!term.deleted_at}"
                                    class="lam-radio-button"
                                    @click.prevent="save_field_for_record_id('term', term.id, !term.default_value, 'default_value')"
                                >
                                    <open-icon glyph="asterisk" />
                                </a>
                            </div>
                            <div :class="{'uk-width-8-10': (edited_dictionary_term_number_of_buttons(term) === 2), 'uk-width-9-10': (edited_dictionary_term_number_of_buttons(term) === 1), 'uk-width-1-1': (edited_dictionary_term_number_of_buttons(term) === 0)}">
                                <div class="uk-grid uk-grid-small">
                                    <div :class="{'uk-width-8-10': edited_dictionary_can_have_weights, 'uk-width-1-1': !edited_dictionary_can_have_weights}">
                                        <form-input
                                            :id="`term-${term.id}-name`"
                                            name="name"
                                            v-model="term.name"
                                            :editable="edited_dictionary_term_is_editable(term, 'name')"
                                            :required="false"
                                            class="uk-width-1-1"
                                            @input="save_field_for_record_id('term', term.id, $event, 'name')"
                                        />
                                    </div>
                                    <div v-if="edited_dictionary_can_have_weights" class="uk-width-2-10">
                                        <form-input
                                            :id="`term-${term.id}-weight`"
                                            name="weight"
                                            type="float"
                                            v-model="term.weight"
                                            :editable="edited_dictionary_term_is_editable(term, 'weight')"
                                            :required="false"
                                            class="uk-width-1-1"
                                            @input="save_field_for_record_id('term', term.id, $event, 'weight')"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div v-if="edited_dictionary_term_is_deletable(term)" class="uk-width-1-10">
                                <base-button
                                    v-if="!term.deleted_at"
                                    color="danger"
                                    @click.prevent="dictionary_term_remove(term)"
                                >
                                    <open-icon glyph="trash" />
                                </base-button>
                                <base-button
                                    v-else
                                    @click.prevent="dictionary_term_restore(term)"
                                >
                                    <open-icon glyph="undo" />
                                </base-button>
                            </div>
                        </div>
                    </li>
                    <li v-if="edited_dictionary_can_add_values">
                        <div class="uk-grid uk-grid-small">
                            <div class="uk-width-9-10">
                                <div class="uk-grid uk-grid-small">
                                    <div :class="{'uk-width-8-10': edited_dictionary_can_have_weights, 'uk-width-1-1': !edited_dictionary_can_have_weights}">
                                        <form-input
                                            id="new-term-name"
                                            name="name"
                                            v-model="new_term.name"
                                            :editable="true"
                                            :required="false"
                                            class="uk-width-1-1"
                                        />
                                    </div>
                                    <div v-if="edited_dictionary_can_have_weights" class="uk-width-2-10">
                                        <form-input
                                            id="new-term-weight"
                                            name="weight"
                                            type="float"
                                            v-model="new_term.weight"
                                            :editable="true"
                                            :required="false"
                                            class="uk-width-1-1"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="uk-width-1-10">
                                <base-button
                                    color="primary"
                                    @click.prevent="dictionary_term_add"
                                >
                                    <open-icon glyph="plus" />
                                </base-button>
                            </div>
                        </div>
                    </li>
                </ul>

                <p v-if="!!edited_dictionary.autosuggest" class="uk-alert uk-alert-warning">
                    Fixing typos, renaming items, adding or deleting values are all going to affect future
                    data entry. The existing data will <em>not</em> be modified.
                </p>
            </form>
        </modal-dialog>

        <modal-dialog
            id="service-editor"
            :show.sync="editing_service"
        >
            <template v-slot:title>
                Lam Service
            </template>

            <form @submit.prevent.stop class="uk-form">
                <div class="uk-grid uk-grid-small">
                    <div class="uk-width-1-1">
                        <form-input
                            id="service-name"
                            name="name"
                            v-model="edited_service.name"
                            :editable="true"
                            :required="false"
                            placeholder="Service Name"
                            class="uk-width-1-1"
                            @input="save_field_for_record_id('service', edited_service.id, $event, 'name')"
                        />
                    </div>
                </div>

                <div class="uk-grid uk-grid-small">
                    <div class="uk-width-1-1">
                        <form-select
                            id="service-description_type"
                            name="description_type"
                            :value="edited_service.description_type"
                            :options="description_types"
                            :editable="true"
                            :required="false"
                            class="uk-width-1-1"
                            @input="save_field_for_record_id('service', edited_service.id, $event.id, 'description_type')"
                        />
                    </div>
                </div>

                <ul v-if="edited_service.description" class="uk-list uk-list-space">
                    <li
                        v-for="(choice, index) in edited_service.description"
                        :key="index"
                    >
                        <div class="uk-grid uk-grid-small">
                            <div class="uk-width-9-10">
                                <form-input
                                    :id="`service-choice-${index}`"
                                    name="choice"
                                    :value="choice"
                                    :editable="true"
                                    :required="false"
                                    class="uk-width-1-1"
                                    @input="service_choice_set(index, $event)"
                                />
                            </div>
                            <div class="uk-width-1-10">
                                <base-button
                                    color="danger"
                                    @click.prevent="service_choice_remove(index)"
                                >
                                    <open-icon glyph="trash" />
                                </base-button>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="uk-grid uk-grid-small">
                            <div class="uk-width-9-10">
                                <form-input
                                    id="new-service-choice"
                                    name="new_service_choice"
                                    v-model="new_service_choice"
                                    :editable="true"
                                    :required="false"
                                    class="uk-width-1-1"
                                />
                            </div>
                            <div class="uk-width-1-10">
                                <base-button
                                    color="primary"
                                    @click.prevent="service_choice_add"
                                >
                                    <open-icon glyph="plus" />
                                </base-button>
                            </div>
                        </div>
                    </li>
                </ul>
            </form>
        </modal-dialog>
    </li>
</template>

<script>
import { mapGetters } from "vuex"
import { orderBy } from "lodash"

import is_setting_page from "./IsSettingPage"

import FormInput from "../../ui/FormInput"
import FormSelect from "../../ui/FormSelect"
import ModalDialog from "../../dialogs/ModalDialog/ModalDialog"
import Awards from "./Awards"

export default {
    mixins: [is_setting_page],
    components: {
        FormInput,
        FormSelect,
        ModalDialog,
        Awards
    },
    methods: {
        load_data () {
            if(this.is_settings_admin) {
                this.$store.dispatch("FETCH_RECORDS", { entity: "category", query: { fields: ["fieldset::default", "project_count"] } })
                this.$store.dispatch("FETCH_RECORDS", { entity: "dictionary" })
                this.$store.dispatch("FETCH_RECORDS", { entity: "service" })
            }
        },
        is_main_keyword_section (section = null) {
            return false
            /*
            if(!section && this.edited_keyword_section) section = this.edited_keyword_section.section
            return section === window.nibnut.vue.application_settings.main_category_section
            */
        },
        keyword_section_edit (section) {
            this.edited_keyword_section = section
            this.new_keyword.section = this.edited_keyword_section.section
            this.new_keyword.name = ""
            this.editing_keyword_section = true
        },
        keyword_section_add () {
            this.keyword_section_edit({ id: 0, section: this.new_keyword_section })
            this.new_keyword_section = ""
        },
        keyword_section_remove (section) {
            this.$confirm({
                type: "warning",
                title: "Delete Keywords List",
                message: `Do you really want to delete ${section.section}? This will delete all keywords under it...`,
                cancel: "Keep It",
                ok: "Delete It",
                then: (confirmed) => {
                    if(confirmed) {
                        this.$store.dispatch("DELETE_KEYWORD_SECTION", {
                            section: section.section
                        }).catch(error => {
                            this.$error(error.message)
                        })
                    }
                }
            })
        },
        keyword_section_change (value) {
            if(this.edited_keyword_section.id) {
                this.save_field_for_record_id("category", this.edited_keyword_section.id, value, "section")
            }
        },
        keyword_section_keyword_add () {
            if(!this.new_keyword || !this.new_keyword.name) this.$error("Please enter a keyword to add to this section.")
            else if(this.raw_category_records.find(keyword => (this.new_keyword.name.toLowerCase() === keyword.name.toLowerCase()))) this.$error("That keyword already exists!")
            else {
                this.$store.dispatch("CREATE_RECORD", {
                    entity: "category",
                    data: this.new_keyword
                }).then(() => {
                    this.new_keyword.name = ""
                })
            }
        },
        keyword_section_keyword_is_deletable (keyword) {
            return !!keyword && !keyword.project_count
        },
        keyword_section_keyword_remove (keyword) {
            this.$confirm({
                type: "warning",
                title: "Delete Keyword",
                message: `Do you really want to delete ${keyword.name}?`,
                cancel: "Keep It",
                ok: "Delete It",
                then: (confirmed) => {
                    if(confirmed) {
                        this.$store.dispatch("RECORD_DELETE", {
                            entity: "category",
                            id: keyword.id
                        }).catch(error => {
                            this.$error(error.message)
                        })
                    }
                }
            })
        },
        dictionary_edit (dictionary) {
            this.$store.dispatch("FETCH_RECORDS", {
                entity: "term",
                query: { dictionary_id: dictionary.id, archived: true, fields: ["fieldset::default", "project_count"] }
            }).then(() => {
                this.edited_dictionary = dictionary
                this.editing_dictionay = true
            })
        },
        edited_dictionary_term_is_editable (term, field = "") {
            if(!this.edited_dictionary) return false
            if(this.edited_dictionary.autosuggest) return !!term && !term.code_constant && !term.deleted_at
            if(field === "name") return !term.deleted_at && this.edited_dictionary_can_edit_labels
            return !term.deleted_at
        },
        edited_dictionary_term_is_deletable (term) {
            if(!this.edited_dictionary || !term) return false
            if(this.edited_dictionary.autosuggest) return !!term && !term.code_constant
            if(this.edited_dictionary.id.match(/^(insider_knowledge)$/)) return true // we know these can handle soft-deleted options
            return (this.edited_dictionary.id !== "project_status") && !term.project_count
        },
        edited_dictionary_term_number_of_buttons (term) {
            let number_of_buttons = 0
            if(this.edited_dictionary_can_have_default) number_of_buttons++
            if(this.edited_dictionary_term_is_deletable(term)) number_of_buttons++
            return number_of_buttons
        },
        dictionary_term_add () {
            if(!this.new_term || !this.new_term.name) this.$error("Please enter a term to add to the dictionary.")
            else {
                this.$store.dispatch("CREATE_RECORD", {
                    entity: "term",
                    data: { dictionary_id: this.edited_dictionary.id, ...this.new_term }
                }).then(() => {
                    this.new_term.name = ""
                    this.new_term.weight = ""
                })
            }
        },
        dictionary_term_remove (term) {
            this.$confirm({
                type: "warning",
                title: "Delete Term",
                message: `Do you really want to delete ${term.name}?`,
                cancel: "Keep It",
                ok: "Delete It",
                then: (confirmed) => {
                    if(confirmed) {
                        this.$store.dispatch("RECORD_DELETE", {
                            entity: "term",
                            id: term.id
                        }).catch(error => {
                            this.$error(error.message)
                        })
                    }
                }
            })
        },
        dictionary_term_restore (term) {
            this.$store.dispatch("RECORD_RESTORE", {
                entity: "term",
                id: term.id
            }).catch(error => {
                this.$error(error.message)
            })
        },
        service_edit (service) {
            this.edited_service = service
            this.editing_service = true
        },
        service_add () {
            if(!this.new_service) this.$error("Please enter a service name to add.")
            else {
                this.$store.dispatch("CREATE_RECORD", {
                    entity: "service",
                    data: { name: this.new_service, description_type: "list" }
                }).then(service => {
                    this.new_service = ""
                    this.service_edit(service)
                })
            }
        },
        service_remove (service) {
            this.$confirm({
                type: "warning",
                title: "Delete Service",
                message: `Do you really want to delete ${service.name}?`,
                cancel: "Keep It",
                ok: "Delete It",
                then: (confirmed) => {
                    if(confirmed) {
                        this.$store.dispatch("RECORD_DELETE", {
                            entity: "service",
                            id: service.id
                        }).catch(error => {
                            this.$error(error.message)
                        })
                    }
                }
            })
        },
        service_choice_set (index, value) {
            const choices = this.edited_service.description
            choices[index] = value
            this.save_field_for_record_id("service", this.edited_service.id, choices, "description")
        },
        service_choice_add () {
            const choices = this.edited_service.description
            choices.push(this.new_service_choice)
            this.save_field_for_record_id("service", this.edited_service.id, choices, "description").then(() => {
                this.new_service_choice = ""
            })
        },
        service_choice_remove (index) {
            const choices = this.edited_service.description
            choices.splice(index, 1)
            this.save_field_for_record_id("service", this.edited_service.id, choices, "description")
        }
    },
    computed: {
        ...mapGetters(["entity_record"]),
        raw_category_records () {
            return this.entity_records("category")
        },
        raw_dictionary_records () {
            return this.entity_records("dictionary").filter(dictionary => dictionary.id !== "awards")
        },
        raw_award_records () {
            return this.entity_records("dictionary").filter(dictionary => dictionary.id === "awards")
        },
        raw_term_records () {
            return this.entity_records("term")
        },
        raw_service_records () {
            return this.entity_records("service")
        },
        keyword_sections () {
            const sections = {}
            if(this.raw_category_records) {
                this.raw_category_records.forEach(category => {
                    if(!sections[category.section]) sections[category.section] = category
                })
            }

            return orderBy(Object.values(sections), category => category.section, "asc")
        },
        edited_keyword_section_keywords () {
            if(!this.edited_keyword_section) return []
            return this.raw_category_records.filter(category => category.section === this.edited_keyword_section.section)
        },
        sorted_dictionaries () {
            return orderBy(this.raw_dictionary_records.filter(dictionary => !dictionary.autosuggest), "name", "asc")
        },
        sorted_autosuggests () {
            return orderBy(this.raw_dictionary_records.filter(dictionary => !!dictionary.autosuggest), "name", "asc")
        },
        sorted_awards () {
            return orderBy(this.raw_award_records, "name", "asc")
        },
        edited_dictionary_can_have_default () {
            if(!this.edited_dictionary) return false
            return (this.edited_dictionary.id !== "project_status") && !this.edited_dictionary.autosuggest
        },
        edited_dictionary_can_edit_labels () {
            if(!this.edited_dictionary || !this.edited_dictionary.id) return false
            return !this.edited_dictionary.id.match(/^(shelf_life)$/i)
        },
        edited_dictionary_can_have_weights () {
            if(!this.edited_dictionary || !this.edited_dictionary.id) return false
            return !!this.edited_dictionary.id.match(/^(type_of_request|insider_knowledge|shelf_life)$/i)
        },
        edited_dictionary_can_add_values () {
            if(!this.edited_dictionary || !this.edited_dictionary.id) return false
            return !this.edited_dictionary.id.match(/^(project_status|shelf_life)$/i)
        },
        edited_dictionary_terms () {
            return this.raw_term_records.filter(term => term.dictionary_id === this.edited_dictionary.id)
        },
        sorted_services () {
            return orderBy(this.raw_service_records, "name", "asc")
        },
        description_types () {
            return [
                { id: "list", name: "List of Choices" },
                { id: "number", name: "Number" },
                { id: "boolean", name: "Yes / No" },
                { id: "text", name: "Freeform Text" }
            ]
        }
    },
    data () {
        return {
            editing_keyword_section: false,
            edited_keyword_section: {},
            new_keyword: { section: "", name: "" },
            new_keyword_section: "",

            edited_dictionary: {},
            editing_dictionay: false,

            new_term: { name: "", weight: "" },

            edited_service: {},
            editing_service: false,

            new_service: "",

            new_service_choice: ""
        }
    }
}
</script>
